import { useCallback, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "react-hot-toast";
import {
  usePayment as useSDKPayment,
  useApplePayCheckout,
  type CheckoutData,
} from "@tagadapay/plugin-sdk/v2";
import type { CheckoutFormData } from "@/src/lib/schemas/checkout-schemas";
import type { PluginConfig } from "@/src/types/plugin";
import { useNavigate } from "react-router-dom";

interface UsePaymentProps {
  form: UseFormReturn<CheckoutFormData>;
  pluginConfig: PluginConfig;
  checkout: CheckoutData | undefined;
  /** Latest checkout after refresh (avoids stale closure in payment callbacks). */
  getCheckout?: () => CheckoutData | undefined;
  prePurchaseCheckout: () => Promise<boolean>;
  refresh?: () => Promise<void>;
  flushAndSaveCheckoutInfo?: (value?: CheckoutFormData) => Promise<void>;
}

export function usePayment({
  form,
  pluginConfig,
  checkout,
  getCheckout,
  prePurchaseCheckout,
  refresh,
  flushAndSaveCheckoutInfo,
}: UsePaymentProps) {
  const navigate = useNavigate();
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);
  const cardPaymentFailureToastShownRef = useRef(false);
  const threeDsLoadingToastIdRef = useRef<string | null>(null);

  const dismissThreeDsLoadingToast = useCallback(() => {
    const id = threeDsLoadingToastIdRef.current;
    if (id) {
      toast.dismiss(id);
      threeDsLoadingToastIdRef.current = null;
    }
  }, []);
  const {
    processCardPayment,
    isLoading: isCardPaymentLoading,
    error: paymentError,
    clearError: clearPaymentError,
  } = useSDKPayment();
  const {
    handleApplePayClick,
    processingPayment: isApplePayProcessing,
    error: applePayError,
    clearError: clearApplePayError,
  } = useApplePayCheckout({
    checkout,
    onSuccess: async (result) => {
      toast.success("Payment successful! 🎉");
      const sessionOrderId = () =>
        getCheckout?.()?.checkoutSession?.orderId ??
        checkout?.checkoutSession?.orderId;
      let orderId =
        result?.order?.id ?? result?.payment?.order?.id ?? sessionOrderId();
      if (orderId) {
        navigate(`/thankyou/${orderId}`);
      } else if (refresh) {
        try {
          await refresh();
          orderId = sessionOrderId();
          if (!orderId) {
            for (let i = 0; i < 3; i++) {
              await new Promise((resolve) => setTimeout(resolve, 300));
              orderId = sessionOrderId();
              if (orderId) break;
            }
          }
          if (orderId) {
            navigate(`/thankyou/${orderId}`);
          } else {
            toast.error(
              "Payment successful but order ID not found. Please contact support.",
              { duration: 8000 },
            );
          }
        } catch {
          toast.error(
            "Payment successful but order ID not found. Please contact support.",
            { duration: 8000 },
          );
        }
      } else {
        toast.error(
          "Payment successful but order ID not found. Please contact support.",
          { duration: 8000 },
        );
      }
    },
    onError: (errorMsg) => {
      toast.error(`Payment failed: ${errorMsg}`);
    },
    onCancel: () => {
      // User cancelled - no toast needed
    },
  });
  const isPaymentLoading =
    isPaymentSubmitting || isCardPaymentLoading || isApplePayProcessing;

  const handlePayment = useCallback(async () => {
    setIsPaymentSubmitting(true);
    try {
      const formData = form.getValues();
      const sameBilling = formData.sameBillingAddress;
      const paymentMethod = formData.paymentMethod ?? "card";

      // Build validation fields array
      const addressFields = [
        "firstName",
        "lastName",
        "email",
        "address1",
        "address2",
        "country",
        "city",
        "state",
        "postal",
      ];

      // Add billing fields if billing address is different
      const billingFields = sameBilling
        ? []
        : [
            "billingFirstName",
            "billingLastName",
            "billingAddress",
            "billingCountry",
            "billingState",
            "billingCity",
            "billingZip",
          ];

      // Validate address form (shipping + billing if different)
      const addressValid = await form.trigger([
        ...addressFields,
        ...billingFields,
      ] as any);

      // Validate card form only when payment method is card
      const cardValid =
        paymentMethod === "apple-pay" ||
        (await form.trigger(["cardNumber", "expiryDate", "cvc"]));

      const errors = form.formState.errors;

      if (!addressValid || !cardValid) {
        const fieldPriority = [
          "firstName",
          "lastName",
          "email",
          "address1",
          "address2",
          "country",
          "city",
          "state",
          "postal",
          "billingFirstName",
          "billingLastName",
          "billingAddress",
          "billingCountry",
          "billingState",
          "billingCity",
          "billingZip",
          "cardNumber",
          "expiryDate",
          "cvc",
        ];

        const firstErrorField = fieldPriority.find(
          (field) => errors[field as keyof typeof errors],
        );

        if (firstErrorField) {
          const scrollToField = () => {
            let fieldElement = document.querySelector(
              `[data-address-field="${firstErrorField}"]`,
            ) as HTMLElement;
            if (!fieldElement) {
              fieldElement = document.querySelector(
                `[data-card-field="${firstErrorField}"]`,
              ) as HTMLElement;
            }

            if (fieldElement) {
              const yOffset = window.innerWidth < 768 ? -120 : -80;
              const y =
                fieldElement.getBoundingClientRect().top +
                window.pageYOffset +
                yOffset;
              window.scrollTo({ top: y, behavior: "smooth" });

              setTimeout(() => {
                fieldElement.focus();
                if (window.innerWidth < 768) {
                  setTimeout(() => {
                    fieldElement.blur();
                    setTimeout(() => fieldElement.focus(), 50);
                  }, 100);
                }
              }, 300);
            }
          };

          if (["cardNumber", "expiryDate", "cvc"].includes(firstErrorField)) {
            form.setFocus(firstErrorField as keyof CheckoutFormData);
            setTimeout(scrollToField, 100);
          } else {
            scrollToField();
          }

          const fieldDisplayNames: Record<string, string> = {
            firstName: "First Name",
            lastName: "Last Name",
            email: "Email Address",
            phone: "Phone Number",
            address1: "Street Address",
            country: "Country",
            city: "City",
            state: "State/Province",
            postal: "Zip/Postal Code",
            billingFirstName: "Billing First Name",
            billingLastName: "Billing Last Name",
            billingAddress: "Billing Address",
            billingCountry: "Billing Country",
            billingState: "Billing State/Province",
            billingCity: "Billing City",
            billingZip: "Billing Zip/Postal Code",
            cardNumber: "Card Number",
            expiryDate: "Expiry Date",
            cvc: "CVC",
          };

          const displayName =
            fieldDisplayNames[firstErrorField] || firstErrorField;
          toast.error(`Please check the ${displayName} field`);
        } else {
          toast.error("Please check all required fields");
        }

        return;
      }

      if (!checkout?.checkoutSession?.id) {
        toast.error("Checkout session not ready. Please try again.");
        return;
      }

      const paymentFormData = form.getValues();

      const enhancedPaymentData = {
        ...paymentFormData,
        state:
          paymentFormData.state && paymentFormData.state.trim()
            ? paymentFormData.state.trim()
            : "N/A",
      };

      const isStateRequired = ["US", "CA", "GB"].includes(
        enhancedPaymentData.country,
      );
      if (isStateRequired && !enhancedPaymentData.state?.trim()) {
        toast.error("Please enter a state/province for the selected country.");
        form.setFocus("state");
        return;
      }

      // Explicitly save address data before processing payment to ensure it's persisted
      // This prevents race conditions with the debounced auto-save
      if (flushAndSaveCheckoutInfo) {
        try {
          await flushAndSaveCheckoutInfo(enhancedPaymentData);
        } catch (error) {
          console.error(
            "[usePayment] Failed to save checkout info before payment:",
            error,
          );
          toast(
            "Address could not be saved before payment. Continuing with checkout—you may re-enter details if needed.",
            { icon: "⚠️", duration: 5000 },
          );
        }
      }

      const prePurchaseSuccess = await prePurchaseCheckout();
      if (!prePurchaseSuccess) {
        toast.error("Failed to pre-purchase checkout. Please try again.");
        return;
      }

      // Apple Pay flow - trigger Apple Pay sheet
      if (paymentMethod === "apple-pay") {
        handleApplePayClick();
        return;
      }

      // Card payment flow
      try {
        const cardData = form.getValues();
        const cardNumber = cardData.cardNumber?.replace(/\s+/g, "") ?? "";
        const expiryDate = cardData.expiryDate ?? "";
        const cvc = cardData.cvc ?? "";
        if (!cardNumber || !expiryDate || !cvc) {
          toast.error("Card details are required");
          return;
        }

        cardPaymentFailureToastShownRef.current = false;

        await processCardPayment(
          checkout.checkoutSession.id,
          {
            cardNumber,
            expiryDate,
            cvc,
          },
          {
            enableThreeds: true,
            initiatedBy: "customer",
            source: "checkout",
            onSuccess: async (response) => {
              dismissThreeDsLoadingToast();
              toast.success("Payment successful! 🎉");

              // Get orderId from response (PaymentResponse has order or payment.order)
              const sessionOrderId = () =>
                getCheckout?.()?.checkoutSession?.orderId ??
                checkout?.checkoutSession?.orderId;

              let orderId =
                response?.order?.id ??
                response?.payment?.order?.id ??
                sessionOrderId();

              // If still not available, refresh checkout and poll for orderId
              if (!orderId && refresh) {
                try {
                  await refresh();

                  for (let i = 0; i < 3; i++) {
                    await new Promise((resolve) => setTimeout(resolve, 300));
                    orderId = sessionOrderId();
                    if (orderId) break;
                  }
                } catch (error) {
                  console.error(
                    "[usePayment] Failed to refresh checkout:",
                    error,
                  );
                }
              }

              if (orderId) {
                navigate(`/thankyou/${orderId}`);
              } else {
                console.error(
                  "[usePayment] OrderId not found after all attempts. Result:",
                  response,
                );
                toast.error(
                  "Payment successful but order ID not found. Please contact support with your payment confirmation.",
                  {
                    duration: 8000,
                  },
                );
              }
            },
            onFailure: (errorMsg: string) => {
              dismissThreeDsLoadingToast();
              if (cardPaymentFailureToastShownRef.current) return;
              cardPaymentFailureToastShownRef.current = true;
              toast.error(`Payment failed: ${errorMsg}`);
            },
            onRequireAction: () => {
              dismissThreeDsLoadingToast();
              threeDsLoadingToastIdRef.current = toast.loading(
                "Please complete the additional security verification...",
              );
            },
          },
        );
      } catch (error) {
        dismissThreeDsLoadingToast();
        if (cardPaymentFailureToastShownRef.current) {
          return;
        }
        cardPaymentFailureToastShownRef.current = true;
        const errorMsg =
          error instanceof Error
            ? error.message
            : "Payment failed. Please try again.";
        toast.error(errorMsg);
      }
    } finally {
      setIsPaymentSubmitting(false);
    }
  }, [
    form,
    checkout,
    getCheckout,
    processCardPayment,
    handleApplePayClick,
    prePurchaseCheckout,
    navigate,
    refresh,
    flushAndSaveCheckoutInfo,
    dismissThreeDsLoadingToast,
  ]);

  // Clear payment errors when user types (card) or switches method (Apple Pay)
  const cardNumber = form.watch("cardNumber");
  const expiryDate = form.watch("expiryDate");
  const cvc = form.watch("cvc");
  const watchedPaymentMethod = form.watch("paymentMethod");

  if (paymentError && (cardNumber || expiryDate || cvc)) {
    clearPaymentError();
  }
  if (applePayError && watchedPaymentMethod === "card") {
    clearApplePayError();
  }

  return {
    handlePayment,
    isPaymentLoading,
    paymentError: paymentError ?? applePayError,
  };
}
